import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { email: "Email", social: "Social", sms: "SMS", phone: "Phone Script" };
const TYPE_CLASS = {
  email: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  social: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  sms: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  phone: "bg-violet-500/10 text-violet-300 border-violet-500/20",
};

export default function AdminMarketingTemplates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", type: "email", content: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/marketing-templates");
      setList(data);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", type: "email", content: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ title: t.title, type: t.type, content: t.content });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title || !form.content) {
      toast.error("Title and content are required"); return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/marketing-templates/${editing.template_id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/admin/marketing-templates", form);
        toast.success("Template created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/marketing-templates/${t.template_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Marketing Templates</h1>
          <p className="mt-1 text-sm text-slate-400">Templates visible to brokers in their Marketing Kit.</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="tpl-new-btn">
          <Plus className="mr-2 h-4 w-4" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {list.map((t) => (
          <Card key={t.template_id} className="border-slate-800 bg-slate-900 p-5" data-testid={`admin-tpl-${t.template_id}`}>
            <div className="mb-3 flex items-center justify-between">
              <Badge variant="outline" className={TYPE_CLASS[t.type]}>{TYPE_LABEL[t.type]}</Badge>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="h-8 w-8 text-slate-300 hover:bg-slate-800" data-testid={`tpl-edit-${t.template_id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => del(t)} className="h-8 w-8 text-rose-300 hover:bg-slate-800" data-testid={`tpl-del-${t.template_id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="text-sm font-semibold text-slate-100">{t.title}</div>
            <pre className="mt-2 line-clamp-5 whitespace-pre-wrap rounded-md border border-slate-800 bg-slate-950 p-3 font-sans text-xs text-slate-400">{t.content}</pre>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-50">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription className="text-slate-400">Templates broadcast to all certified brokers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-slate-800 bg-slate-950 text-slate-50" data-testid="tpl-title" />
            </div>
            <div>
              <Label className="text-slate-300">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 border-slate-800 bg-slate-950" data-testid="tpl-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="social">Social</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="phone">Phone Script</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Content</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1 min-h-[180px] border-slate-800 bg-slate-950 text-slate-50" data-testid="tpl-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800">Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="tpl-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
